/* One isolated controller per section; custom elements also reconnect in Theme Editor. */
if (!customElements.get('ecommerce-social-proof-carousel')) {
  customElements.define('ecommerce-social-proof-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const on = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: this.abort.signal });
      this.viewport = this.querySelector('.ep-viewport');
      this.track = this.querySelector('.ep-track');
      this.cards = [...this.track.children];
      this.count = this.cards.length;
      this.controls = this.querySelector('.ep-controls');
      this.pauseButton = this.querySelector('[data-pause]');
      this.position = this.querySelector('.ep-position');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.editor = Boolean(window.Shopify?.designMode);
      this.speed = Number(this.dataset.speed) || 15;
      this.userPaused = this.dataset.autoplay !== 'true' || this.motion.matches;
      this.hovered = this.focused = this.editing = false;
      this.visible = true;
      this.x = 0;
      this.lastTime = 0;
      this.rebuild();
      on(this.querySelector('[data-previous]'), 'click', () => this.move(-1));
      on(this.querySelector('[data-next]'), 'click', () => this.move(1));
      on(this.pauseButton, 'click', () => { this.userPaused = !this.userPaused; this.updatePause(); });
      on(this, 'pointerenter', event => { if (event.pointerType === 'mouse') this.hovered = true; });
      on(this, 'pointerleave', () => { this.hovered = false; });
      on(this, 'focusin', event => { this.focused = this.viewport.contains(event.target) && this.viewport.matches(':focus-visible'); });
      on(this, 'focusout', event => { this.focused = this.viewport.contains(event.relatedTarget) && this.viewport.matches(':focus-visible'); });
      on(this.viewport, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); this.move(event.key === 'ArrowLeft' ? 1 : -1);
      });
      on(this.viewport, 'pointerdown', event => this.startDrag(event));
      on(this.viewport, 'pointermove', event => this.drag(event), { passive: false });
      on(window, 'pointerup', event => this.endDrag(event));
      on(this.viewport, 'pointercancel', event => this.endDrag(event));
      on(this.viewport, 'lostpointercapture', event => {
        // Touch starts on the image; ignore its bubbled event when capture moves to the viewport.
        if (event.target === this.viewport) this.endDrag(event);
      });
      on(this.viewport, 'dragstart', event => event.preventDefault());
      on(this.motion, 'change', event => { if (event.matches) this.userPaused = true; this.updatePause(); });
      on(document, 'visibilitychange', () => { this.lastTime = 0; });
      on(document, 'shopify:block:select', event => {
        const index = this.cards.findIndex(card => card.dataset.testimonialId === event.detail.blockId);
        if (index < 0) return;
        this.editing = true; this.tween = null; this.x = this.cycle + index * this.step; this.paint();
      });
      on(document, 'shopify:block:deselect', event => {
        if (this.cards.some(card => card.dataset.testimonialId === event.detail.blockId)) this.editing = false;
      });
      this.resize = new ResizeObserver(() => this.rebuild());
      this.resize.observe(this.viewport);
      this.intersection = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.lastTime = 0; });
      this.intersection.observe(this);
      this.updatePause();
      this.tick = this.tick.bind(this);
      this.frame = requestAnimationFrame(this.tick);
    }

    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.resize?.disconnect(); this.intersection?.disconnect();
      cancelAnimationFrame(this.frame);
      this.track?.querySelectorAll('[data-clone]').forEach(card => card.remove());
      this.classList.remove('ep-ready', 'ep-single', 'ep-dragging');
      if (this.track) this.track.style.transform = '';
    }

    rebuild() {
      if (!this.count || !this.viewport.clientWidth) return;
      const oldStep = this.step;
      const offset = oldStep ? ((this.x - this.cycle) / oldStep) : 0;
      this.track.querySelectorAll('[data-clone]').forEach(card => card.remove());
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      this.step = this.cards[0].getBoundingClientRect().width + gap;
      this.cycle = this.step * this.count;
      const edgePeek = !oldStep && !this.editor && this.viewport.clientWidth >= 1100 ? this.step - gap - 36 : 0;
      this.x = this.cycle + offset * this.step + edgePeek;
      this.tween = null;
      this.classList.toggle('ep-single', this.count === 1);
      this.controls.hidden = this.count < 2;
      if (this.count > 1) {
        // Enough trailing cycles cover wide viewports even with only two screenshots.
        const cycles = Math.ceil(this.viewport.clientWidth / this.cycle) + 3;
        for (let cycle = 1; cycle < cycles; cycle++) {
          const group = document.createDocumentFragment();
          for (const card of this.cards) {
            const clone = card.cloneNode(true);
            clone.dataset.clone = 'true';
            clone.removeAttribute('data-testimonial-id');
            clone.removeAttribute('data-shopify-editor-block');
            clone.setAttribute('aria-hidden', 'true');
            clone.inert = true;
            clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
            group.append(clone);
          }
          if (cycle === 1) this.track.prepend(group);
          else this.track.append(group);
        }
      } else this.x = 0;
      this.classList.add('ep-ready');
      this.paint();
    }

    paint() {
      if (this.count > 1) this.x = this.cycle + ((this.x - this.cycle) % this.cycle + this.cycle) % this.cycle;
      this.track.style.transform = `translate3d(${-this.x}px, 0, 0)`;
      const index = this.count > 1 ? Math.floor((this.x - this.cycle + 1) / this.step) % this.count : 0;
      const text = `${index + 1} / ${this.count}`;
      if (this.position.textContent !== text) this.position.textContent = text;
    }

    move(direction) {
      if (this.count < 2) return;
      this.dragState = null;
      this.classList.remove('ep-dragging');
      this.resumeAt = performance.now() + 1800;
      if (this.motion.matches) { this.x += direction * this.step; this.paint(); return; }
      this.tween = { remaining: direction * this.step, duration: 380 };
    }

    startDrag(event) {
      if (this.count < 2 || !event.isPrimary || event.button !== 0) return;
      this.tween = null;
      this.dragState = { id: event.pointerId, x: event.clientX, y: event.clientY, previousX: event.clientX, active: false };
    }

    drag(event) {
      const state = this.dragState;
      if (!state || state.id !== event.pointerId) return;
      const dx = event.clientX - state.x, dy = event.clientY - state.y;
      if (!state.active) {
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 7) { this.dragState = null; return; }
        if (Math.abs(dx) < 7) return;
        state.active = true;
        this.viewport.setPointerCapture(event.pointerId);
        this.classList.add('ep-dragging');
      }
      if (event.cancelable) event.preventDefault();
      this.x += state.previousX - event.clientX;
      state.previousX = event.clientX;
      this.paint();
    }

    endDrag(event) {
      if (!this.dragState || this.dragState.id !== event.pointerId) return;
      this.dragState = null;
      this.classList.remove('ep-dragging');
      this.resumeAt = performance.now() + 1200;
      if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
    }

    updatePause() {
      this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
      this.pauseButton.setAttribute('aria-label', this.dataset.language === 'en' ? (this.userPaused ? 'Resume automatic scrolling' : 'Pause automatic scrolling') : (this.userPaused ? 'تشغيل الحركة التلقائية' : 'إيقاف الحركة التلقائية'));
    }

    tick(time) {
      const elapsed = this.lastTime ? Math.min(time - this.lastTime, 64) : 0;
      this.lastTime = time;
      if (this.visible && !document.hidden && this.count > 1 && !this.dragState) {
        if (this.tween) {
          const fraction = Math.min(elapsed / this.tween.duration, 1);
          const delta = this.tween.remaining * fraction;
          this.x += delta; this.tween.remaining -= delta; this.tween.duration -= elapsed;
          if (this.tween.duration <= 0) this.tween = null;
          this.paint();
        } else if (!this.userPaused && !this.hovered && !this.focused && !this.editing && time >= (this.resumeAt || 0)) {
          this.x += this.speed * elapsed / 1000;
          this.paint();
        }
      }
      this.frame = requestAnimationFrame(this.tick);
    }
  });
}
